import { computed, inject, Injectable, signal } from '@angular/core';
import { UserService } from '../../shared/services/user.service';
import { tap, switchMap, forkJoin, catchError, of } from 'rxjs';
import { UserDataResponse } from '../../shared/interfaces/user-data-response.interface';
import { UserDataDetailResponse } from '../../shared/interfaces/user-data-detail-response.interface';
import { UserRecognitionsDiplomasResponse } from '../../shared/interfaces/user-recognitions-diplomas-response.interface';
import { CatContests } from '../../how-do-i-win/enums/cat-contest.enum';
import { UserPointsHistoryResponse } from '../../shared/interfaces/user-points-history-response.interface';
import { UserHistoryAwardResponse } from '../../shared/interfaces/user-history-award-response.interface';

@Injectable({ providedIn: 'root' })
export class UserDataService {
  private userService = inject(UserService);

  private _userData = signal<UserDataResponse | null>(null);
  private _userDataDetail = signal<UserDataDetailResponse | null>(null);
  private _userDiplomas = signal<UserRecognitionsDiplomasResponse | null>(null);
  private _nationalSales = signal<UserPointsHistoryResponse[]>([]);
  private _addOns = signal<UserPointsHistoryResponse[]>([]);
  private _dynamo = signal<UserPointsHistoryResponse[]>([]);
  private _historyAwards = signal<UserHistoryAwardResponse[]>([]);
  private _challenges = signal<UserPointsHistoryResponse[]>([]);
  
  userData = computed(() => this._userData());
  userDataDetail = computed(() => this._userDataDetail());
  userName = computed(
    () =>
      `${this._userData()?.name} ${this._userData()?.lastName} ${
        this._userData()?.secondLastName
      }`
  );
  hasInfoComplete = computed(
    () =>
      this._userData()?.cellphoneNumber !== null &&
      this._userData()?.cellphoneNumber !== ''
  );
  userDiplomas = computed(() => this._userDiplomas());
  nationalSales = computed(() => this._nationalSales());
  addOns = computed(() => this._addOns());
  dynamo = computed(() => this._dynamo());
  historyAwards = computed(() => this._historyAwards());
  challenges = computed(() => this._challenges() )
  constructor() {
    this.getUserInfo();
    this.getAchievements();
  }

  getUserInfo() {
    this.userService
      .getUserData()
      .pipe(
        tap((userData) => this._userData.set(userData)),
        switchMap(() => this.userService.getUserDataDetail())
      )
      .subscribe({
        next: (value) => this._userDataDetail.set(value),
      });
  }

  getAchievements() {
    forkJoin({
      diplomas: this.userService.getUserRecognitionsDiploma().pipe(
        catchError(() => {
          return of(null);
        })
      ),
    }).subscribe({
      next: (resp) => {
        if(resp.diplomas?.year != 0 && resp.diplomas?.name != ''){
          this._userDiplomas.set(resp.diplomas);
        }
        
      },
    });
  }

  getUserHistory() {
    forkJoin({
      nationalSales: this.userService.getUserHistoryPointsByCatContestId(
        CatContests.NationalSalesContest
      ),
      addOns: this.userService.getUserHistoryPointsByCatContestId(
        CatContests.AddOns
      ),
      dynamo: this.userService.getUserHistoryPointsByCatContestId(
        CatContests.Dynamo
      ),
      challenges: this.userService.getUserHistoryPointsByCatContestId(
        CatContests.Challenges
      ),
      historyAwards: this.userService.getUserHistoryAwards(),
    }).subscribe({
      next: (resp) => {
        this._nationalSales.set(resp.nationalSales);
        this._addOns.set(resp.addOns);
        this._dynamo.set(resp.dynamo);
        this._challenges.set(resp.challenges);
        this._historyAwards.set(resp.historyAwards);
      },
    });
  }
}
